import { decomposeColor, hexToRgb, rgbToHex } from '@mui/material/styles';

/**
 * Adjusts the lightness and saturation of a color by multiplying HSL components.
 * Pass `1` for either amount to leave that component unchanged.
 */
export function adjustLightAndSat(color: string, lightAmount: number, satAmount: number): string {
  const { type } = decomposeColor(color);
  if (type === 'color') color = hexToRgb(color);
  const { values } = decomposeColor(color);

  const hsl = rgbToHsl(values[0], values[1], values[2]);

  hsl[1] = Math.min(100, Math.max(0, hsl[1] * satAmount));
  hsl[2] = Math.min(100, Math.max(0, hsl[2] * lightAmount));

  const rgb = hslToRgb(hsl[0], hsl[1], hsl[2]);

  let adjustedColor = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  if (type === 'color') {
    adjustedColor = rgbToHex(adjustedColor);
  }
  return adjustedColor;
}

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

export function hslToRgb(h: number, s: number, l: number) {
  let r, g, b;

  h /= 360;
  s /= 100;
  l /= 100;

  function hue2rgb(p: number, q: number, t: number) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export function hexToRGBA(hexCode: string, opacity: number) {
  let hex = hexCode.replace('#', '');

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function addOpacity(color: string, opacity: number) {
  const { type } = decomposeColor(color);
  if (type === 'color') color = hexToRgb(color);
  const { values: rgb } = decomposeColor(color);

  const adjustedColor = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
  return adjustedColor;
}
